/* eslint-disable react/jsx-no-literals, complexity */
import React from 'react';
import { Chart as ChartJS, ArcElement } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { addDays } from 'date-fns';
import { useIntl } from 'react-intl';

import Button from 'carbon-react/lib/components/button';
import Icon, { IconType } from 'carbon-react/lib/components/icon';
import Typography from 'carbon-react/lib/components/typography';

import { UISystemIcon } from '@futrli/ui-components/components/icons/custom/system-icon';

import { AnalysisFeedbackEntryPoint } from '../analysis-feedback/analysis-feedback-entry-point';
import { AnalysisFeedbackRating } from '../analysis-feedback/analysis-feedback-types';

import styles from './business-health-card.scss';

ChartJS.register(ArcElement);

// ─── Status colours ───────────────────────────────────────────────────────────
const COLOR_HEALTHY = '#00811F';
const COLOR_WATCH = '#D64309';
const COLOR_CRITICAL = '#DB004E';
const COLOR_TRACK = 'rgba(0, 0, 0, 0.1)';

type OverallStatus = 'green' | 'yellow' | 'red';

const STATUS: Record<OverallStatus, { color: string; scoreClass: string }> = {
  green: { color: COLOR_HEALTHY, scoreClass: styles.donutScoreGreen },
  yellow: { color: COLOR_WATCH, scoreClass: styles.donutScoreYellow },
  red: { color: COLOR_CRITICAL, scoreClass: styles.donutScoreRed }
};

// Donut chart constants
const DONUT_SIZE = 160;
const DONUT_CUTOUT = '82%';
const DONUT_ROTATION = -90;
const DONUT_BORDER_RADIUS_FILLED = 20;
const SCORE_MIN = 0;
const SCORE_MAX = 100;
const NEXT_UPDATE_OFFSET_DAYS = 30;
const DATE_FORMAT = { day: '2-digit' as const, month: '2-digit' as const, year: 'numeric' as const };

type StatusIndicatorVariant = 'positive' | 'warning' | 'negative';

const STATUS_INDICATOR_ICONS: Record<StatusIndicatorVariant, { type: IconType; color: string }> = {
  positive: { type: 'tick_circle', color: COLOR_HEALTHY },
  warning: { type: 'warning', color: COLOR_WATCH },
  negative: { type: 'error', color: COLOR_CRITICAL }
};

// ─── Types ────────────────────────────────────────────────────────────────────
export interface StatusIndicator {
  label: string;
  colorVariant: StatusIndicatorVariant;
}

export interface BusinessHealthCardProps {
  score: number;
  overallStatus: OverallStatus;
  title: string;
  description: string;
  statusIndicators?: StatusIndicator[];
  createdAt?: string;
  feedbackEnabled?: boolean;
  hasFeedback?: boolean;
  onFeedbackOpen?: (rating?: AnalysisFeedbackRating) => void;
  onViewInsights?: () => void;
  selectedFeedbackRating?: AnalysisFeedbackRating | null;
}

export const BusinessHealthCard = ({
  score,
  overallStatus,
  title,
  description,
  statusIndicators,
  createdAt,
  feedbackEnabled = true,
  hasFeedback = false,
  onFeedbackOpen = () => undefined,
  onViewInsights,
  selectedFeedbackRating = null
}: BusinessHealthCardProps) => {
  const intl = useIntl();
  const { color: statusColor, scoreClass } = STATUS[overallStatus];
  const clampedScore = Math.max(SCORE_MIN, Math.min(SCORE_MAX, score));

  const updatedDate = createdAt ? new Date(createdAt) : undefined;
  const nextUpdateDate = updatedDate ? addDays(updatedDate, NEXT_UPDATE_OFFSET_DAYS) : undefined;
  const formattedUpdatedDate = updatedDate ? intl.formatDate(updatedDate, DATE_FORMAT) : undefined;
  const formattedNextUpdateDate = nextUpdateDate && nextUpdateDate.getTime() >= Date.now()
    ? intl.formatDate(nextUpdateDate, DATE_FORMAT)
    : undefined;

  const donutData = {
    datasets: [
      {
        data: [clampedScore, SCORE_MAX - clampedScore],
        backgroundColor: [statusColor, COLOR_TRACK],
        borderWidth: 0,
        borderRadius: [DONUT_BORDER_RADIUS_FILLED, 0] as [number, number]
      }
    ]
  };

  const donutOptions = {
    responsive: false,
    cutout: DONUT_CUTOUT,
    rotation: DONUT_ROTATION,
    circumference: 360,
    plugins: {
      tooltip: { enabled: false },
      datalabels: { display: false }
    }
  };

  return (
    <div className={styles.cardContainer}>
      <div className={styles.keyline} />
      <div className={styles.cardContent}>
        <div className={styles.header}>
          {formattedUpdatedDate && (
            <Typography fontSize="14px" fontWeight="400" lineHeight="150%" mb={0} color="var(--colorsUtilityYin055)">
              {formattedNextUpdateDate
                ? intl.formatMessage(
                  {
                    id: 'aiInsights.healthCard.updatedAndNextUpdate',
                    defaultMessage: 'Updated: {updatedDate} | Next update: {nextUpdateDate}'
                  },
                  { updatedDate: formattedUpdatedDate, nextUpdateDate: formattedNextUpdateDate }
                )
                : intl.formatMessage(
                  {
                    id: 'aiInsights.healthCard.updated',
                    defaultMessage: 'Updated: {updatedDate}'
                  },
                  { updatedDate: formattedUpdatedDate }
                )}
            </Typography>
          )}
          <Typography fontSize="24px" fontWeight="700" lineHeight="125%" mb={0}>
            {intl.formatMessage({ id: 'aiInsights.healthCard.title', defaultMessage: 'Business Health' })}
          </Typography>
        </div>

        <hr className={styles.divider} />

        <div className={styles.body}>
          <div className={styles.donutContainer}>
            <Doughnut
              data={donutData}
              options={donutOptions}
              width={DONUT_SIZE}
              height={DONUT_SIZE}
              aria-label="Health Score Gauge"
            />
            <div className={styles.donutLabel}>
              <Typography fontSize="24px" fontWeight="500" lineHeight="150%" mb={0} className={scoreClass}>
                {clampedScore}%
              </Typography>
            </div>
          </div>

          <div className={styles.textContent}>
            <div className={styles.textDetails}>
              <Typography fontSize="21px" fontWeight="500" lineHeight="125%" mb={0}>
                {title}
              </Typography>
              <Typography fontSize="16px" fontWeight="400" lineHeight="150%" opacity={0.65} mb={0}>
                {description}
              </Typography>
              {statusIndicators && statusIndicators.length > 0 && (
                <div className={styles.statusSummary}>
                  <strong className={styles.insightsLabel}>
                    {intl.formatMessage({ id: 'aiInsights.healthCard.insights', defaultMessage: 'Insights:' })}
                  </strong>
                  <span className={styles.statusTags}>
                    {statusIndicators.map(indicator => {
                      const icon = STATUS_INDICATOR_ICONS[indicator.colorVariant];
                      return (
                        <span key={indicator.label} className={styles.statusTag}>
                          <Icon type={icon.type} color={icon.color} aria-hidden focusable={false} />
                          {indicator.label}
                        </span>
                      );
                    })}
                  </span>
                </div>
              )}
            </div>

            <div className={styles.buttonRow}>
              <Button buttonType="primary" onClick={onViewInsights} className={styles.viewInsightsButton}>
                {intl.formatMessage({
                  id: 'aiInsights.healthCard.viewDetailedBreakdown',
                  defaultMessage: 'View detailed breakdown'
                })}
              </Button>
            </div>
          </div>
        </div>

        <hr className={styles.divider} />

        <div className={styles.footer}>
          <div className={styles.aiLabel}>
            <UISystemIcon iconName="ai-sparkle" width={20} height={20} />
            <Typography
              color="light-dark(#212121, #f4f4f4)"
              fontSize="12px"
              fontWeight="400"
              lineHeight="150%"
              mb={0}
            >
              {intl.formatMessage({ id: 'aiInsights.healthCard.assistedByAI', defaultMessage: 'Assisted by AI' })}
            </Typography>
          </div>
          {feedbackEnabled && (
            <AnalysisFeedbackEntryPoint
              hasFeedback={hasFeedback}
              onOpen={onFeedbackOpen}
              selectedRating={selectedFeedbackRating}
            />
          )}
        </div>
      </div>
    </div>
  );
};
